const STORAGE_KEY = 'boost:user-preferences';

/**
 * Schema of all user preferences, grouped by feature.
 * Add new blocks/keys here as the product grows.
 * When a backend endpoint appears, the same shape can be sent/received via API.
 */
interface EnumValue {
  id: number | string;
  name: string;
  translate?: string;
}

export interface UserPreferencesData {
  ai?: {
    sidebar_width?: number;
  };
  admin?: {
    /** an admin under a user's avatar sees the menu as that user does */
    view_as_user?: boolean;
  };
  regional?: {
    timezone?: string | null;
    locale?: EnumValue | null;
    language?: EnumValue | null;
    time_format?: EnumValue | null;
  };
}

const DEFAULTS: DeepRequired<UserPreferencesData> = {
  ai: {
    sidebar_width: 420,
  },
  admin: {
    view_as_user: false,
  },
  regional: {
    timezone: '',
    locale: null,
    language: null,
    time_format: null,
  },
};

/** Recursively make all properties required and non-nullable */
type DeepRequired<T> = {
  [K in keyof T]-?: T[K] extends object ? DeepRequired<T[K]> : NonNullable<T[K]>;
};

/** Dot-notation paths: 'ai.sidebarWidth' */
type DotPath<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends object ? DotPath<T[K], `${Prefix}${K}.`> : `${Prefix}${K}`;
}[keyof T & string];

export type PreferencePath = DotPath<DeepRequired<UserPreferencesData>>;

/** Resolve the value type at a dot-notation path */
type ValueAtPath<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? ValueAtPath<NonNullable<T[K]>, Rest>
    : never
  : P extends keyof T
    ? NonNullable<T[P]>
    : never;

export type PreferenceValue<P extends PreferencePath> = ValueAtPath<
  DeepRequired<UserPreferencesData>,
  P
>;

const getStorage = () => (typeof window === 'undefined' ? null : window.localStorage);

const readAll = (): UserPreferencesData => {
  const storage = getStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const writeAll = (data: UserPreferencesData): void => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota exceeded — silently ignore */
  }
};

const getByPath = (obj: Record<string, unknown>, path: string): unknown => {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
};

const setByPath = (obj: Record<string, unknown>, path: string, value: unknown): void => {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  current[keys[keys.length - 1]] = value;
};

/** Listeners subscribed via `UserPreferences.subscribe()` */
type Listener = () => void;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

export const UserPreferences = {
  /** Get a preference value by dot-path (falls back to default). */
  get<P extends PreferencePath>(path: P): PreferenceValue<P> {
    const all = readAll();
    const value = getByPath(all as Record<string, unknown>, path);
    if (value !== undefined) return value as PreferenceValue<P>;
    return getByPath(DEFAULTS as Record<string, unknown>, path) as PreferenceValue<P>;
  },

  /** Set a preference value by dot-path. */
  set<P extends PreferencePath>(path: P, value: PreferenceValue<P>): void {
    const all = readAll() as Record<string, unknown>;
    setByPath(all, path, value);
    writeAll(all as UserPreferencesData);
    notify();
  },

  /** Return full preferences object (for future API sync). */
  getAll(): DeepRequired<UserPreferencesData> {
    const stored = readAll() as Record<string, unknown>;
    return deepMerge(
      DEFAULTS as unknown as Record<string, unknown>,
      stored,
    ) as DeepRequired<UserPreferencesData>;
  },

  /** Replace all preferences at once (e.g. after loading from backend). */
  setAll(data: UserPreferencesData): void {
    writeAll(data);
    notify();
  },

  /** Subscribe to changes. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
} as const;

function deepMerge(
  defaults: Record<string, unknown>,
  overrides: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...defaults };
  for (const key of Object.keys(overrides)) {
    const val = overrides[key];
    if (
      val &&
      typeof val === 'object' &&
      !Array.isArray(val) &&
      defaults[key] &&
      typeof defaults[key] === 'object'
    ) {
      result[key] = deepMerge(
        defaults[key] as Record<string, unknown>,
        val as Record<string, unknown>,
      );
    } else if (val !== undefined) {
      result[key] = val;
    }
  }
  return result;
}
